/**
 * Column Profile Panel - shows statistics and distributions for the selected column.
 * Allows quick filtering via clicks on charts and frequency bars.
 */

import React, { useState } from 'react';
import { useTheme, fontSize, fontWeight, fontFamily, spacing, Stack, Button, Select } from '@fti/design-system';
import type { ColumnProfilePanelProps } from './contracts';
import { formatNumber, formatDate, TYPE_BADGE } from './format';
import { Histogram } from './charts/Histogram';
import { FrequencyBars } from './charts/FrequencyBars';

export function ColumnProfilePanel({ column, profile, onAddFilter }: ColumnProfilePanelProps) {
  const { theme } = useTheme();
  // null = not chosen yet (default to the first path); '' = the element itself.
  const [selectedElementPath, setSelectedElementPath] = useState<string | null>(null);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  // Section heading style matching LexisNexis.tsx labels
  const headingStyle: React.CSSProperties = {
    fontSize: fontSize.xs,
    color: mutedColor,
    textTransform: 'uppercase',
    fontWeight: fontWeight.semibold,
    marginBottom: spacing.xs,
    marginTop: spacing.md,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: fontSize.xs,
    color: mutedColor,
  };

  const valueStyle: React.CSSProperties = {
    fontSize: fontSize.sm,
    color: textColor,
  };

  // Type badge
  const badgeText = TYPE_BADGE[column.type] || column.type;
  const badgeStyle: React.CSSProperties = {
    fontSize: fontSize.xs,
    padding: '2px 6px',
    borderRadius: '4px',
    background: theme.colors.neutral[100],
    color: mutedColor,
    fontWeight: fontWeight.semibold,
  };

  // Missing percentage
  const missingPct = profile.base > 0 ? ((profile.missing / profile.base) * 100).toFixed(1) : '0.0';

  // Stat row component
  const StatRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{value}</div>
    </div>
  );

  return (
    <div style={{ padding: spacing.md, overflow: 'auto', height: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: spacing.md }}>
        <div
          style={{
            fontFamily: fontFamily.mono,
            fontWeight: fontWeight.semibold,
            fontSize: fontSize.sm,
            color: textColor,
            wordWrap: 'break-word',
            marginBottom: spacing.xs,
          }}
        >
          {column.id}
        </div>
        <div style={{ display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
          <span style={badgeStyle}>{badgeText}</span>
          <span style={{ fontSize: fontSize.xs, color: mutedColor }}>Based on {profile.base} rows</span>
        </div>
      </div>

      {/* Basic stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: spacing.md, marginBottom: spacing.md }}>
        <StatRow label="Count" value={formatNumber(profile.count)} />
        <StatRow
          label="Missing"
          value={`${formatNumber(profile.missing)} (${missingPct}%)`}
        />
        <StatRow label="Distinct" value={formatNumber(profile.distinct)} />
      </div>

      {/* Missing filter actions */}
      {profile.missing > 0 && (
        <div style={{ marginBottom: spacing.md }}>
          <Button
            variant="link"
            size="sm"
            onClick={() =>
              onAddFilter({
                id: crypto.randomUUID(),
                column: column.id,
                kind: 'missing',
                present: false,
              })
            }
            style={{ padding: 0, marginRight: spacing.sm }}
          >
            Show missing only
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={() =>
              onAddFilter({
                id: crypto.randomUUID(),
                column: column.id,
                kind: 'missing',
                present: true,
              })
            }
            style={{ padding: 0 }}
          >
            Hide missing
          </Button>
        </div>
      )}

      {/* Type-specific sections */}
      {profile.number && (
        <>
          <div style={headingStyle}>Distribution</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: spacing.md, marginBottom: spacing.md }}>
            <StatRow label="Min" value={formatNumber(profile.number.min)} />
            <StatRow label="Max" value={formatNumber(profile.number.max)} />
            <StatRow label="Mean" value={formatNumber(profile.number.mean)} />
            <StatRow label="Median" value={formatNumber(profile.number.median)} />
            <StatRow label="Std" value={formatNumber(profile.number.std)} />
          </div>
          <Histogram
            bins={profile.number.histogram}
            formatX={formatNumber}
            onBinClick={(b) =>
              onAddFilter({
                id: crypto.randomUUID(),
                column: column.id,
                kind: 'range',
                min: b.x0,
                max: b.x1,
              })
            }
          />
        </>
      )}

      {profile.date && (
        <>
          <div style={headingStyle}>Distribution</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: spacing.md, marginBottom: spacing.md }}>
            <StatRow label="Earliest" value={formatDate(new Date(profile.date.min).getTime())} />
            <StatRow label="Latest" value={formatDate(new Date(profile.date.max).getTime())} />
            {profile.date.invalid > 0 && <StatRow label="Invalid" value={formatNumber(profile.date.invalid)} />}
          </div>
          <Histogram
            bins={profile.date.histogram}
            formatX={(ms) => formatDate(ms)}
            onBinClick={(b) => {
              const fromMs = b.x0;
              const toMs = Math.max(b.x0, b.x1 - 1);
              onAddFilter({
                id: crypto.randomUUID(),
                column: column.id,
                kind: 'dateRange',
                from: new Date(fromMs).toISOString().slice(0, 10),
                to: new Date(toMs).toISOString().slice(0, 10),
              });
            }}
          />
        </>
      )}

      {profile.text && (
        <>
          <div style={headingStyle}>Text Length</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: spacing.md, marginBottom: spacing.md }}>
            <StatRow label="Min" value={formatNumber(profile.text.length.min)} />
            <StatRow label="Mean" value={formatNumber(profile.text.length.mean)} />
            <StatRow label="Max" value={formatNumber(profile.text.length.max)} />
          </div>
          <div style={headingStyle}>Top Values</div>
          <FrequencyBars
            items={profile.text.top}
            total={profile.count}
            onItemClick={(v) =>
              onAddFilter({
                id: crypto.randomUUID(),
                column: column.id,
                kind: 'in',
                values: [v],
              })
            }
          />
        </>
      )}

      {profile.boolean && (
        <>
          <div style={headingStyle}>Values</div>
          <FrequencyBars
            items={[
              { value: 'true', count: profile.boolean.true },
              { value: 'false', count: profile.boolean.false },
            ]}
            total={profile.count}
            onItemClick={(v) =>
              onAddFilter({
                id: crypto.randomUUID(),
                column: column.id,
                kind: 'in',
                values: [v === 'true'],
              })
            }
          />
        </>
      )}

      {profile.array && (
        <>
          <div style={headingStyle}>Length Distribution</div>
          <Histogram
            bins={profile.array.lengthHistogram}
            formatX={formatNumber}
            onBinClick={(b) => {
              // For integer bins where x1 = x0+1, use min=max=x0
              const min = b.x1 === b.x0 + 1 ? b.x0 : Math.ceil(b.x0);
              const max = b.x1 === b.x0 + 1 ? b.x0 : Math.floor(b.x1);
              onAddFilter({
                id: crypto.randomUUID(),
                column: column.id,
                kind: 'arrayLength',
                min,
                max,
              });
            }}
          />
          {profile.array.topElements.length > 0 && (
            <>
              <div style={headingStyle}>Top Elements</div>
              <div style={{ marginBottom: spacing.sm }}>
                <Select
                  size="sm"
                  // The DS Select treats '' as no selection; '.' stands for the element itself.
                  value={(selectedElementPath ?? profile.array.topElements[0]?.path ?? '') || '.'}
                  onChange={(v) => setSelectedElementPath(v === '.' ? '' : v)}
                  options={profile.array.topElements.map((te) => ({
                    value: te.path || '.',
                    label: te.path === '' ? '(element)' : te.path,
                  }))}
                />
              </div>
              {(() => {
                const selectedPath = selectedElementPath ?? profile.array.topElements[0]?.path ?? '';
                const topElement = profile.array.topElements.find((te) => te.path === selectedPath);
                return topElement ? (
                  <FrequencyBars
                    items={topElement.values}
                    total={profile.count}
                    onItemClick={(v) =>
                      onAddFilter({
                        id: crypto.randomUUID(),
                        column: column.id,
                        kind: 'arrayElement',
                        elementPath: selectedPath,
                        op: 'eq',
                        value: v,
                      })
                    }
                  />
                ) : null;
              })()}
            </>
          )}
        </>
      )}

      {profile.mixed && (
        <>
          <div style={headingStyle}>Type Counts</div>
          <div style={{ marginBottom: spacing.md }}>
            {Object.entries(profile.mixed).map(([kind, count]) => (
              <div key={kind} style={{ fontSize: fontSize.sm, color: textColor }}>
                {kind}: {formatNumber(count)}
              </div>
            ))}
          </div>
          {profile.text && profile.text.top.length > 0 && (
            <>
              <div style={headingStyle}>Top Values</div>
              <FrequencyBars
                items={profile.text.top}
                total={profile.count}
                onItemClick={(v) =>
                  onAddFilter({
                    id: crypto.randomUUID(),
                    column: column.id,
                    kind: 'contains',
                    text: v,
                  })
                }
              />
            </>
          )}
        </>
      )}

      {column.type === 'object' && (
        <div style={{ fontSize: fontSize.sm, color: mutedColor, marginTop: spacing.md }}>
          Expand the group or open a record to inspect nested values.
        </div>
      )}
    </div>
  );
}
